import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { api } from "../lib/api";
import type { AuthUser, MagicLinkResponse } from "../types/domain";

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  refreshSession: () => Promise<void>;
  requestMagicLink: (
    email: string,
    turnstileToken: string,
  ) => Promise<MagicLinkResponse>;
  consumeMagicLink: (token: string) => Promise<AuthUser>;
  updatePseudo: (pseudo: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const hasExplicitAuth = useRef(false);

  async function refreshSession() {
    const nextUser = await api.getMe();
    setUser(nextUser);
  }

  useEffect(() => {
    void api
      .getMe()
      .then((nextUser) => {
        if (!hasExplicitAuth.current) {
          setUser(nextUser);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const value: AuthContextValue = {
    user,
    loading,
    refreshSession,
    async requestMagicLink(email, turnstileToken) {
      return await api.requestMagicLink({ email, turnstileToken });
    },
    async consumeMagicLink(token) {
      hasExplicitAuth.current = true;
      const nextUser = await api.consumeMagicLink(token);
      setUser(nextUser);
      return nextUser;
    },
    async updatePseudo(pseudo) {
      const nextUser = await api.updateProfile(pseudo);
      setUser(nextUser);
      return nextUser;
    },
    async logout() {
      await api.logout();
      setUser(null);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
